/* eslint-disable no-unused-vars */
import React from 'react';
import { Pressable } from 'react-native';
import Animated, { FadeInLeft } from 'react-native-reanimated';
import PlusIcon from '../16pxIcons/PlusIcon';
import ResolvedIcon from '../16pxIcons/ResolvedIcon';
import PenIcon16 from '../16pxIcons/PenIcon16';
import RectangleIcon from '../16pxIcons/RectangleIcon';
import EllipseIcon from '../16pxIcons/EllipseIcon';
import UndoIcon from '../16pxIcons/UndoIcon';
import BroomIcon from '../16pxIcons/BroomIcon';
import DeleteIcon16 from '../16pxIcons/DeleteIcon16';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';

type DrawToolsProps = {
  areaComplete: boolean;
  editingShape: boolean;
  drawPoints: Array<{ x: number; y: number; number: number }>;
  redoPoints: Array<any>;
  drawerStyle?: any;
  setCreateModalVisible: (visible: boolean) => void;
  logEvent: (...args: any[]) => void;
  setToolDialogVisible: (visible: boolean) => void;
  toolDialogVisible: boolean;
  setSelectedTool: (tool: string) => void;
  setDrawPoints: (points: any[]) => void;
  setAreaComplete: (complete: boolean) => void;
  setRedoPoints: (points: any[]) => void;
  setDrawMode: (mode: boolean) => void;
  setEditingShape: (editing: boolean) => void;
  setAreas: (areas: any[]) => void;
  setSpaces: (spaces: any[]) => void;
  setBeingEdited: (area: any) => void;
  selectedTool: string;
  areas: any[];
  spaces: any[];
  beingEdited: any;
  selectedArea: any;
};

const SelectedToolIcon = (selectedTool) => {
  if (selectedTool === 'rectangle') {
    return <RectangleIcon color={Colors.light.hereworksBlue500} />;
  }
  if (selectedTool === 'ellipse') {
    return <EllipseIcon color={Colors.light.hereworksBlue500} />;
  }
  return <PenIcon16 color={Colors.light.hereworksBlue500} />;
};

const DrawTools: React.FC<DrawToolsProps> = ({
  areaComplete,
  editingShape,
  drawPoints,
  redoPoints,
  drawerStyle,
  setCreateModalVisible,
  logEvent,
  setToolDialogVisible,
  toolDialogVisible,
  setSelectedTool,
  setDrawPoints,
  setAreaComplete,
  setRedoPoints,
  setDrawMode,
  setEditingShape,
  setAreas,
  setSpaces,
  setBeingEdited,
  selectedTool,
  areas,
  spaces,
  beingEdited,
  selectedArea,
}) => (
  <Animated.View
    style={[{
      borderWidth: 1,
      borderColor: areaComplete
        ? Colors.light.hereworksGreen400
        : Colors.light.hereworksBlack100,
      borderRadius: 48,
      marginLeft: 24,
      backgroundColor: '#fff',
      alignItems: 'center',
      paddingHorizontal: 4,
    }, drawerStyle, Style.circleShadow]}>
    <Pressable
      style={(state) => [
        Style.zoomButton,
        Style.toolButton,
        {
          backgroundColor: areaComplete
            ? Colors.light.hereworksGreen400
            : Colors.light.hereworksBlack100,
          borderColor: areaComplete
            ? Colors.light.hereworksGreen400
            : Colors.light.hereworksBlack100,
          opacity: state.hovered ? 0.5 : 1,
        },
      ]}
      disabled={!areaComplete}
      onPress={() => {
        logEvent('Map', 'plus_icon');
        setCreateModalVisible(true);
      }}
      testID='map-add-area-on-map-btn'>
      {!editingShape && <PlusIcon />}
      {editingShape && <ResolvedIcon color="#fff" />}
    </Pressable>
    <Pressable
      style={() => [Style.zoomButton, Style.toolButton]}
      onPress={() => {
        logEvent('Map', 'tool_dialog');
        setToolDialogVisible(!toolDialogVisible);
      }}
      testID='map-tool-btn'>
      <SelectedToolIcon selectedTool={selectedTool} />
      {toolDialogVisible && <Animated.View
        style={{
          position: 'absolute',
          left: 50,
          backgroundColor: '#fff',
          borderWidth: 1,
          borderColor: Colors.light.hereworksBlue100,
          borderRadius: 4,
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'row',
        }}
        entering={FadeInLeft}
      >
        <Pressable
          style={{ padding: 8 }}
          testID='map-pen-tool-btn'
          onPress={() => {
            logEvent('Map', 'pen_icon');
            setSelectedTool('draw');
            setToolDialogVisible(false);
          }}>
          <PenIcon16 color={Colors.light.hereworksBlue500} />
        </Pressable>
        <Pressable
          style={{ padding: 8 }}
          testID='map-rectangle-tool-btn'
          onPress={() => {
            logEvent('Map', 'rect_icon');
            setSelectedTool('rectangle');
            setToolDialogVisible(false);
            const rectanglePoints = [
              { x: 100, y: 100, number: 0 },
              { x: 100, y: 200, number: 1 },
              { x: 200, y: 200, number: 2 },
              { x: 200, y: 100, number: 3 },
              { x: 100, y: 100, number: 4 },
            ];
            setDrawPoints(rectanglePoints);
            setAreaComplete(true);
          }}>
          <RectangleIcon />
        </Pressable>
        <Pressable
          style={{ padding: 8 }}
          testID='map-ellipse-tool-btn'
          onPress={() => {
            logEvent('Map', 'ellipse_icon');
            setSelectedTool('ellipse');
            setToolDialogVisible(false);
            const ellipsePoints = [
              { x: 100, y: 100, number: 0 },
              { x: 100, y: 200, number: 1 },
              { x: 200, y: 200, number: 2 },
              { x: 200, y: 100, number: 3 },
              { x: 100, y: 100, number: 4 },
            ];
            setDrawPoints(ellipsePoints);
            setAreaComplete(true);
          }}>
          <EllipseIcon />
        </Pressable>
      </Animated.View>}
    </Pressable>
    <Pressable
      style={(state) => [
        Style.zoomButton,
        Style.toolButton,
        {
          opacity: (drawPoints.length === 0 || state.hovered || selectedTool !== 'draw') ? 0.2 : 1,
        }]}
      onPress={() => {
        logEvent('Map', 'undo');
        const newArr = Object.assign([], drawPoints);
        const newRedo = [...redoPoints];
        newRedo.push(newArr[newArr.length - 1]);
        setRedoPoints(newRedo);
        newArr.splice(newArr.length - 1, 1);
        setDrawPoints(newArr);
        setAreaComplete(false);
      }}
      disabled={drawPoints.length === 0 || selectedTool !== 'draw'}
      testID='map-previous-area-state-btn'>
      <UndoIcon />
    </Pressable>
    <Pressable
      style={(state) => [
        Style.zoomButton,
        Style.toolButton,
        {
          opacity: (redoPoints.length === 0 || state.hovered) ? 0.2 : 1,
          transform: [{ rotateY: '180deg' }],
        },
      ]}
      onPress={() => {
        logEvent('Map', 'redo');
        const newArr = Object.assign([], drawPoints);
        newArr.push(redoPoints.pop());
        if (
          newArr.length > 1
          && newArr[0].x === newArr[newArr.length - 1].x
          && newArr[0].y === newArr[newArr.length - 1].y
        ) {
          setAreaComplete(true);
        }
        newArr.forEach((item, index) => {
          item.number = index;
        });
        setDrawPoints(newArr);
      }}
      disabled={redoPoints.length === 0}
      testID='map-next-area-state-btn'>
      <UndoIcon />
    </Pressable>
    <Pressable
      style={(state) => [
        Style.zoomButton,
        Style.toolButton,
        {
          opacity: (drawPoints.length === 0 || state.hovered) ? 0.2 : 1,
        }]}
      onPress={() => {
        logEvent('Map', 'clear');
        if (selectedTool === 'rectangle' || selectedTool === 'ellipse') {
          const rectanglePoints = [
            { x: 100, y: 100, number: 0 },
            { x: 100, y: 200, number: 1 },
            { x: 200, y: 200, number: 2 },
            { x: 200, y: 100, number: 3 },
            { x: 100, y: 100, number: 4 },
          ];
          setDrawPoints(rectanglePoints);
          setAreaComplete(true);
        } else {
          setDrawPoints([]);
          setAreaComplete(false);
        }
      }}
      disabled={drawPoints.length === 0}
      testID='map-clear-btn'>
      <BroomIcon />
    </Pressable>
    <Pressable
      style={(state) => [
        Style.zoomButton,
        Style.toolButton,
        {
          backgroundColor: Colors.light.hereworksRed50,
          borderColor: Colors.light.hereworksLightPink,
          opacity: state.hovered ? 0.5 : 1,
        },
      ]}
      onPress={() => {
        logEvent('Map', 'cancel_draw');
        setSelectedTool('draw');
        setDrawPoints([]);
        setAreaComplete(false);
        setDrawMode(false);
        setEditingShape(false);
        const newAreas = Object.assign([], areas);
        const newSpaces = Object.assign([], spaces);
        if (beingEdited !== null) {
          if (selectedArea) {
            newSpaces.push(beingEdited);
            setSpaces(newSpaces);
          } else {
            newAreas.push(beingEdited);
            setAreas(newAreas);
          }
          setBeingEdited(null);
        }
      }}
      testID='map-close-edit-area-modal-btn'>
      <DeleteIcon16 color={Colors.light.hereworksRed500} />
    </Pressable>
  </Animated.View>
);

export default DrawTools;
